// src/features/patients/AISummaryPanel.tsx
// The AI-generated patient briefing — extracted from PatientRecords.tsx's
// own private, inline-styled AISummaryPanel (2026-09-17 Consultation
// redesign, Phase 3) into one shared component with two layouts:
//   full    — Patient Records' own tab. Same states/behavior as before,
//             ported to Card/Button/Badge/Tailwind (required either way:
//             this file is under features/**, which the ESLint
//             design-system gate already covers — the old inline styles
//             can't move here as-is).
//   compact — a slim strip under the Consultation banner, in the space
//             Phase 1 freed up when the old AlertStrip moved into
//             PatientBanner. Collapsed by default; expands to a 4-column
//             grid + a one-line clinical overview.
// GET .../summary runs on mount either way (cached, no AI call); the AI is
// only ever invoked by an explicit click on Generate/Regenerate — this
// component never auto-generates (Consultation already fires its own single
// AI draft elsewhere when AI is on, see ConsultationChat.tsx).
import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { getAiSettings } from '@/api';
import { loadConfig, getActiveModel } from '@/aiConfig';
import { getAuthUser } from '@/lib/api/session';
import { getPatientSummary, generatePatientSummary, SummaryError, type PatientSummaryData } from './api';

export interface AISummaryPanelProps {
  uhid: string;
  variant: 'full' | 'compact';
}

const SECTIONS = [
  { key: 'CLINICAL OVERVIEW', title: 'Clinical overview', icon: '🏥', full: true, warn: false },
  { key: 'ACTIVE CONDITIONS', title: 'Active conditions', icon: '🩺', full: false, warn: false },
  { key: 'CURRENT MEDICATIONS', title: 'Current medications', icon: '💊', full: false, warn: false },
  { key: 'KEY LAB FINDINGS', title: 'Key lab findings', icon: '🧪', full: false, warn: false },
  { key: 'RISK FLAGS', title: 'Risk flags', icon: '⚠️', full: false, warn: true },
  { key: 'NEXT STEPS', title: 'Next steps', icon: '📋', full: false, warn: false },
] as const;

const GENERATING_STEPS = [
  'Reviewing every visit & consultation',
  'Extracting data from uploaded documents',
  'Analysing vitals trends over time',
  'Synthesising clinical briefing',
];

/** Same regex-header parsing the original panel used — Phase 3's own plan
 * defers "return JSON sections instead" to a later pass. */
function getSection(text: string, key: string): string {
  const rx = new RegExp(`${key}[:\\s]*(.*?)(?=\\n[A-Z ]+:|$)`, 'si');
  const m = text.match(rx);
  return m ? m[1]!.trim() : '';
}

/** The Clinical overview comes back as one free-text paragraph (or, from a
 * model already inclined to itemise, several dash/bullet-prefixed lines) —
 * either way, the compact strip wants short, scannable points, not a wall of
 * prose. Existing bulleted lines are used as-is; a single paragraph is split
 * on sentence boundaries so each point still reads as a full clinical
 * statement rather than an arbitrary word-count chunk. */
function toBulletPoints(text: string): string[] {
  if (!text) return [];
  const lines = text.split('\n').map((l) => l.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  if (lines.length > 1) return lines;
  return text.split(/(?<=[.!?])\s+(?=[A-Z(])/).map((s) => s.trim()).filter(Boolean);
}

function hideStorageKey(uhid: string): string {
  return `hms_summary_hidden:${getAuthUser()?.id ?? 'anon'}:${uhid}`;
}

function readHidden(uhid: string): boolean {
  try { return localStorage.getItem(hideStorageKey(uhid)) === '1'; } catch { return false; }
}

function writeHidden(uhid: string, hidden: boolean): void {
  try {
    if (hidden) localStorage.setItem(hideStorageKey(uhid), '1');
    else localStorage.removeItem(hideStorageKey(uhid));
  } catch { /* private browsing / storage blocked — the preference just won't persist */ }
}

export function AISummaryPanel({ uhid, variant }: AISummaryPanelProps) {
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const [data, setData] = useState<PatientSummaryData | null>(null);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [nextStepsOpen, setNextStepsOpen] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    getAiSettings().then((s) => setAiEnabled(!!s.ai_processing_enabled)).catch(() => setAiEnabled(false));
  }, []);

  useEffect(() => {
    setData(null); setExpanded(false); setError(null);
    getPatientSummary(uhid).catch(() => null).then((d) => setData(d || { summary: null, generated_at: null, stale: true, source_count: 0 }));
  }, [uhid]);

  useEffect(() => {
    if (variant === 'compact') setHidden(readHidden(uhid));
  }, [uhid, variant]);

  const generate = async () => {
    setGenerating(true); setError(null); setExpanded(true);
    try {
      const aiCfg = loadConfig();
      setData(await generatePatientSummary(uhid, getActiveModel(aiCfg)));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
    setGenerating(false);
  };

  const hasSummary = !!data?.summary;
  const toggle = () => { if (generating) return; if (hasSummary) setExpanded((v) => !v); else void generate(); };
  const consentRequired = error instanceof SummaryError && error.code === 'AI_CONSENT_REQUIRED';

  const sections = useMemo(() => {
    if (!data?.summary) return [];
    const text = data.summary;
    return SECTIONS.map((sec) => ({ ...sec, content: getSection(text, sec.key) })).filter((s) => s.content);
  }, [data?.summary]);

  // Computed here (before any early return below) rather than beside its one
  // use in the compact variant's JSX — hooks must run in the same order every
  // render, and the compact branch has two early returns (hidden, no data
  // yet) above where the bullets are actually shown.
  const overviewLine = sections.find((s) => s.key === 'CLINICAL OVERVIEW')?.content ?? '';
  const overviewBullets = useMemo(() => toBulletPoints(overviewLine), [overviewLine]);

  // Compact never renders anything while the hospital's AI switch is off (or
  // still unknown) — a strip that would just 403 the moment it's clicked
  // adds nothing under a busy Consultation banner. Full still renders a
  // one-line notice: a doctor visiting Patient Records deliberately should
  // see *why* there's no summary, not a section that silently vanished.
  if (aiEnabled === false || (variant === 'compact' && aiEnabled !== true)) {
    if (variant === 'compact') return null;
    return (
      <div className="mt-5 border-t border-border pt-4 text-[12px] text-ink-muted">
        AI-assisted summaries are turned off for this hospital.
      </div>
    );
  }

  const consentNotice = (
    <div className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
      This patient hasn&apos;t granted AI-processing consent yet.{' '}
      <a href="#" className="font-semibold underline underline-offset-2" onClick={(e) => e.preventDefault()}>
        Record consent in Patient Records ↗
      </a>
    </div>
  );

  const sectionGrid = (cols: readonly (typeof SECTIONS)[number]['key'][]) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {sections.filter((s) => cols.includes(s.key) as boolean).map((sec) => (
        <div
          key={sec.key}
          className={cn(
            'rounded-lg border p-3',
            sec.warn ? 'border-warn/40 bg-warn-soft' : 'border-success/30 bg-success-soft'
          )}
        >
          <div className={cn('mb-1.5 text-[11px] font-bold uppercase tracking-wide', sec.warn ? 'text-warn-ink' : 'text-teal')}>
            {sec.icon} {sec.title}
          </div>
          {sec.content.split('\n').filter(Boolean).map((line, i) => (
            <div
              key={i}
              className={cn(
                'mb-1 border-l-2 pl-2 text-[13px] leading-relaxed',
                sec.warn ? 'border-danger/40 text-warn-ink' : 'border-success/40 text-ink'
              )}
            >
              {line.replace(/^[-•*]\s*/, '')}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const generatingSkeleton = (
    <div className="flex flex-col gap-2 py-2">
      {GENERATING_STEPS.map((step, i) => (
        <div
          key={step}
          className={cn(
            'flex items-center gap-2.5 rounded-md border px-3 py-2',
            i === 0 ? 'border-success/40 bg-success-soft' : 'border-border bg-muted'
          )}
        >
          <span className={cn('text-sm', i === 0 && 'animate-pulse')}>{i === 0 ? '⏳' : '⚪'}</span>
          <span className={cn('text-[13px]', i === 0 ? 'text-success-ink' : 'text-ink-muted')}>{step}</span>
        </div>
      ))}
    </div>
  );

  if (variant === 'full') {
    return (
      <div className="mt-5 border-t border-border pt-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <Button size="sm" onClick={toggle} disabled={generating || !data}>
            {generating ? '⏳ Analysing full patient record…' : hasSummary ? (expanded ? '▲ Hide AI summary' : '🤖 View AI summary') : '🤖 Generate AI summary'}
          </Button>
          {hasSummary && expanded && (
            <Button size="sm" variant="ghost" onClick={generate} disabled={generating}>↺ Regenerate</Button>
          )}
          {hasSummary && data.generated_at && (
            <span className="text-[11px] text-ink-muted">Generated {new Date(data.generated_at).toLocaleString('en-IN')}</span>
          )}
          {hasSummary && data.stale && !generating && (
            <span className="text-[11px] font-semibold text-warn-ink">⚠ New records since last generated</span>
          )}
        </div>

        {!hasSummary && !generating && !error && (
          <div className="mt-2 max-w-xl text-[12px] leading-relaxed text-ink-muted">
            Synthesises every visit, the full vitals history, approved consultations/prescriptions,
            allergies, structured diagnosis/procedure/medication history, and uploaded document OCR
            data into a holistic clinical briefing — the story a consultant should know before seeing
            this patient.
          </div>
        )}

        {error && (consentRequired
          ? <div className="mt-3">{consentNotice}</div>
          : <ErrorState className="mt-3" message={error.message} onRetry={generate} />
        )}

        {expanded && generating && generatingSkeleton}

        {expanded && !generating && hasSummary && (
          <div className="fade-in mt-3.5 flex flex-col gap-3">
            <Badge variant="success">✓ AI-generated · For clinical reference only</Badge>
            {sections.filter((s) => s.key === 'CLINICAL OVERVIEW').map((sec) => (
              <div key={sec.key} className="rounded-lg border border-success/30 bg-success-soft p-3.5 text-[13px] leading-relaxed text-ink">
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-teal">{sec.icon} {sec.title}</div>
                {sec.content}
              </div>
            ))}
            {sectionGrid(['ACTIVE CONDITIONS', 'CURRENT MEDICATIONS', 'KEY LAB FINDINGS', 'RISK FLAGS', 'NEXT STEPS'])}
          </div>
        )}
      </div>
    );
  }

  // ── compact ──────────────────────────────────────────────────
  if (hidden) {
    return (
      <button
        type="button"
        onClick={() => { writeHidden(uhid, false); setHidden(false); }}
        className="self-start text-[12px] font-medium text-ink-muted underline-offset-2 hover:underline"
      >
        Show AI summary
      </button>
    );
  }

  if (!data) {
    return <Skeleton className="h-9 w-full rounded-lg" />;
  }

  return (
    <Card className="p-3.5">
      {/* The Clinical overview is always shown in full, never truncated — a
          doctor should be able to read the actual gist without clicking
          anything. "View details" only ever reveals the *other* sections
          (active conditions/medications/lab findings/risk flags/next
          steps) — it never gates the overview itself. Bulleted, two columns:
          a single dense paragraph took longer to scan than the same facts
          broken into points a doctor can pick out at a glance. */}
      {hasSummary && !generating && (
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-ink-muted">
            <span aria-hidden="true">🤖</span> Patient Summary
          </div>
          {overviewBullets.length > 0 ? (
            <ul className="grid list-disc grid-cols-1 gap-x-5 gap-y-1 pl-4 text-[13px] leading-relaxed text-ink sm:grid-cols-2">
              {overviewBullets.map((point, i) => <li key={i}>{point}</li>)}
            </ul>
          ) : (
            <p className="text-[13px] leading-relaxed text-ink">Summary ready — view details.</p>
          )}
        </div>
      )}

      <div className={cn('flex flex-wrap items-center gap-2.5', hasSummary && !generating && 'mt-2')}>
        {!hasSummary && (
          <>
            <span aria-hidden="true">🤖</span>
            {!generating && !error && (
              <>
                <span className="text-[12px] text-ink-muted">No AI summary yet for this patient.</span>
                <Button size="sm" onClick={generate}>🤖 Generate</Button>
              </>
            )}
          </>
        )}
        {error && !consentRequired && (
          <span className="text-[12px] text-danger">{error.message} <button type="button" className="font-semibold underline" onClick={generate}>Retry</button></span>
        )}
        {error && consentRequired && <span className="text-[12px] text-warn-ink">AI consent required for this patient.</span>}
        {generating && <span className="text-[12px] text-ink-muted">Generating… reading visits, vitals, notes, documents</span>}
        {hasSummary && data.stale && !generating && (
          <>
            <span className="shrink-0 text-[12px] font-semibold text-warn-ink">⚠ New records</span>
            <Button size="sm" variant="ghost" onClick={generate}>↺ Regenerate</Button>
          </>
        )}
        <div className="flex-1" />
        {hasSummary && !generating && (
          <button type="button" className="shrink-0 text-[12px] font-medium text-teal underline-offset-2 hover:underline" onClick={toggle}>
            {expanded ? '▲ Collapse' : 'View details ▸'}
          </button>
        )}
        {!expanded && !generating && (
          <button
            type="button"
            className="shrink-0 text-[12px] text-ink-muted underline-offset-2 hover:underline"
            onClick={() => { writeHidden(uhid, true); setHidden(true); }}
          >
            Hide
          </button>
        )}
      </div>

      {error && consentRequired && <div className="mt-2.5">{consentNotice}</div>}

      {generating && <div className="mt-2.5"><Skeleton className="h-16 w-full rounded-lg" /></div>}

      {expanded && !generating && hasSummary && (
        <div className="fade-in mt-3 flex flex-col gap-3">
          {data.generated_at && (
            <div className="text-[11px] text-ink-muted">Generated {new Date(data.generated_at).toLocaleString('en-IN')}</div>
          )}
          {sectionGrid(['ACTIVE CONDITIONS', 'CURRENT MEDICATIONS', 'KEY LAB FINDINGS', 'RISK FLAGS'])}
          <div>
            <button
              type="button"
              className="text-[12px] font-medium text-teal underline-offset-2 hover:underline"
              onClick={() => setNextStepsOpen((v) => !v)}
            >
              {nextStepsOpen ? '▾ Next steps' : 'Next steps ▸'}
            </button>
            {nextStepsOpen && sections.filter((s) => s.key === 'NEXT STEPS').map((sec) => (
              <div key={sec.key} className="mt-1.5 rounded-lg border border-success/30 bg-success-soft p-3 text-[13px] leading-relaxed text-ink">
                {sec.content.split('\n').filter(Boolean).map((line, i) => (
                  <div key={i} className="mb-1 border-l-2 border-success/40 pl-2">{line.replace(/^[-•*]\s*/, '')}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
